/**
 * Phase 8.12 — REST-driven timed duel run.
 *
 * Visual language mirrors `live_room.js` so a player who's used
 * to live trivia doesn't get thrown by the async duel format —
 * same `Question N of M` header, same 4px violet-to-pink progress
 * bar, same choice-button styling with locked / reveal states.
 *
 * The whole question set is pinned server-side at duel-create and
 * returned by `POST /duels/{id}/run/start`. Cycle per question:
 *   1. Answering — timer counts down; player picks or times out.
 *   2. Submit — POST the pick to `/run/answer`; server scores.
 *   3. Reveal — briefly highlight the pick + show points earned.
 *   4. Next question (or finish).
 *
 * Idempotency: the server enforces "one answer per (run, order)"
 * and returns the stored answer on retry, so a network blip doesn't
 * corrupt scoring. On refresh mid-run we resume from
 * `duel.runs[me].answered`.
 */

import { api, ApiError } from '../api.js';
import { startCollector } from '../lib/client_signals.js';
import { escape } from '../components/form.js';
import { mountDecoyBlock, mountQuestionView } from '../lib/question_view.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';

// Ms between the answer lock and advancing to the next question.
// Live trivia's reveal waits for the slowest player; async doesn't
// have to, so we keep the beat tight.
const REVEAL_MS = 1800;
// How often the timer + progress bar redraw during answering.
const TICK_MS = 100;
// Guard against phantom taps in the first ~350ms of a question — a
// mobile scroll-vs-tap can register as an accidental choice before
// the player has read the prompt. Matches match.js TAP_WARMUP_MS.
const TAP_WARMUP_MS = 350;

export async function renderDuelRun(root) {
  const duelId = window.location.pathname.match(/^\/duel\/([^/]+)\/play$/)?.[1];
  if (!duelId) {
    navigate('/duels');
    return;
  }
  root.innerHTML = `
    <div class="appbar">
      <a href="/duel/${escape(duelId)}" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Duel · your round</div>
    </div>
    <div class="pad" id="run-page"><div class="skel"></div><div class="skel"></div></div>
  `;
  const page = document.getElementById('run-page');

  let payload;
  try {
    payload = await api(`/duels/${duelId}/run/start`, { method: 'POST' });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    page.innerHTML = `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }

  const { duel, run, questions } = payload;
  if (run.status === 'submitted' || run.status === 'forfeited') {
    // Already played — bounce to the status screen.
    navigate(`/duel/${duelId}`);
    return;
  }
  const cursor = Math.min(Number(run.answered || 0), questions.length);

  // Rehydrate 50/50 state from the run row so a refresh mid-run
  // restores the used cap and the struck-out choices. `usedOrders` is
  // the source of truth for "cap hit"; `eliminatedByOrder` mirrors the
  // two struck indices per question the server has issued.
  const usedOrders = Array.isArray(run.fiftyfifty_used_orders)
    ? run.fiftyfifty_used_orders.map(Number)
    : [];
  const state = {
    duelId,
    windowMs: Number(duel.answer_window_ms || 15000),
    score: Number(run.score || 0),
    answered: cursor,
    total: questions.length,
    fiftyfiftyBalance: Number(payload.fiftyfifty_balance || 0),
    fiftyfiftyUsedOrders: usedOrders,
    // Populated per-question after a successful spend. Not persisted
    // across refresh for prior questions (the reveal has already fired
    // by then so the striked look is moot), only for the current one.
    eliminatedByOrder: {},
    submittingFiftyfifty: false,
  };

  await loop(page, questions, state);
}

async function loop(page, questions, state) {
  while (state.answered < state.total) {
    const q = questions[state.answered];
    const scored = await runOneQuestion(page, q, state);
    state.score = Number(scored.score ?? state.score + Number(scored.score_awarded || 0));
    state.answered = Number(scored.answered ?? state.answered + 1);
  }
  page.innerHTML = `
    <div class="card center" style="padding:22px;">
      <div style="font-family:var(--font-display);font-size:15px;color:#fff;font-weight:600;margin-bottom:6px">
        Submitting your run…
      </div>
      <div style="font-size:12px;color:var(--mute)">Scoring the last answers.</div>
    </div>
  `;
  try {
    await api(`/duels/${state.duelId}/run/finish`, { method: 'POST' });
  } catch (err) {
    toast.error(err.message || 'Could not submit run.');
  }
  navigate(`/duel/${state.duelId}`);
}

/**
 * Serve one question through answering → reveal.
 * Returns the /run/answer response so the loop can keep state in sync.
 */
async function runOneQuestion(page, question, state) {
  const startedAt = performance.now();
  const deadline = startedAt + state.windowMs;
  let selectedIndex = null;
  let picked = false;
  // Handle to the currently-mounted question view. Every repaint()
  // (initial + after a 50/50 spend) recreates the host div and re-mounts
  // a fresh view. The old handle is dropped; any pending onSelect from
  // the old shadow just calls the same runOneQuestion-scoped closure,
  // which is picked-guarded.
  let currentView = null;
  // Anti-cheat telemetry (see lib/client_signals.js) — one collector per
  // question, anchored to the question mount. Snapshotted on click; on
  // timeout without a click, snapshotted with `answerEvent: null` so
  // the server still gets `visibility_changes` / `overlay_detected`.
  const collector = startCollector({ questionRenderedAt: startedAt });
  let clientSignals = null;

  const applyCurrentState = () => {
    if (!currentView) return;
    const eliminated = state.eliminatedByOrder[question.order] || [];
    if (eliminated.length) currentView.setEliminated(eliminated);
    if (picked && selectedIndex != null) currentView.lockAnswer(selectedIndex);
  };

  const mountAnsweringView = () => {
    const host = page.querySelector('#duel-qv-host');
    if (!host) return;
    currentView = mountQuestionView(host, {
      mode: 'duel',
      onSelect: (idx, ev) => {
        // Same tap-warmup + one-shot guards as the old click handler —
        // scroll-vs-tap in the first 350ms shouldn't count as an answer.
        if (picked) return;
        if (performance.now() - startedAt < TAP_WARMUP_MS) return;
        picked = true;
        selectedIndex = idx;
        // Snapshot BEFORE lockAnswer — captures the state as it stood
        // at the moment of click, before the optimistic-lock repaint
        // rearranges the shadow tree.
        clientSignals = collector.snapshot({ answerEvent: ev });
        currentView.lockAnswer(idx);
      },
    });
    currentView.setQuestion({
      prompt: question.prompt,
      choices: question.choices,
      twoColumnLayout: !!question.image_url,
    });
    applyCurrentState();
    // Decoy trap — light-DOM siblings inside the qv-slot wrapper. Only
    // mounted when the player hasn't picked yet; once picked, the real
    // click already went through the shadow, and any subsequent decoy
    // click would be noise. Body innerHTML swaps (repaint after 50/50,
    // paintReveal) GC the previous decoy block along with the wrapper.
    if (!picked) {
      const slot = host.parentElement;
      if (slot) {
        mountDecoyBlock(slot, {
          onDecoyHit: () => collector.markDecoyHit(),
        });
      }
    }
  };

  const repaint = () => {
    paintAnswering(page, question, state, { deadline });
    mountAnsweringView();
    wireFiftyfifty(page, state, question, { onDone: repaint });
  };

  repaint();

  await new Promise((resolve) => {
    const tick = () => {
      const remaining = Math.max(0, deadline - performance.now());
      updateTimer(page, remaining, state.windowMs);
      if (picked || remaining <= 0) {
        clearInterval(tickTimer);
        resolve();
      }
    };
    tick();
    const tickTimer = setInterval(tick, TICK_MS);
  });

  const elapsed = Math.min(state.windowMs, Math.round(performance.now() - startedAt));
  // If the player timed out without picking, `clientSignals` is still
  // null. Drain the collector now so we send whatever visibility_changes
  // / overlay_detected it observed (the anti-cheat pipeline wants to see
  // that a bot's "timeout" was actually a normal quiet 15s — or wasn't).
  if (clientSignals === null) {
    clientSignals = collector.snapshot({ answerEvent: null });
  }

  let scored;
  try {
    scored = await api(`/duels/${state.duelId}/run/answer`, {
      method: 'POST',
      body: {
        order: question.order,
        selected_index: selectedIndex,
        elapsed_ms: elapsed,
        client_signals: clientSignals,
      },
    });
  } catch (err) {
    toast.error(err.message || 'Answer not saved.');
    // Fallback shape so the loop still advances — no reveal is
    // rendered (no is_correct signal to trust).
    scored = {
      is_correct: false,
      correct_index: -1,
      score_awarded: 0,
      selected_index: selectedIndex,
      run: { score: state.score, answered: state.answered + 1 },
    };
  }

  // Reveal — same behaviour as live trivia: correct choice is
  // highlighted green regardless of what the player picked; if the
  // player picked wrong, their choice is highlighted pink. Server's
  // `correct_index` is safe to expose here — the answer for THIS
  // order is already locked-in and cannot be changed.
  paintReveal(page, question, {
    picked: selectedIndex,
    isCorrect: !!scored.is_correct,
    scoreAwarded: Number(scored.score_awarded || 0),
    totalScore: Number(scored.run?.score ?? state.score),
    questionNum: state.answered + 1,
    total: state.total,
  });
  // Reveal view mount — separate handle from the answering view. The
  // answering view is now unreachable (paintReveal replaced its host
  // div), so garbage collection will drop it and its shadow root.
  const revealHost = page.querySelector('#duel-qv-host');
  if (revealHost) {
    const revealView = mountQuestionView(revealHost, {
      mode: 'duel',
      // Reveal choices are inert divs, not buttons — onSelect never
      // fires. Left as a no-op for clarity.
      onSelect: () => {},
    });
    revealView.setQuestion({
      prompt: question.prompt,
      choices: question.choices,
      twoColumnLayout: !!question.image_url,
    });
    revealView.showReveal({
      correctIndex: Number.isFinite(Number(scored.correct_index)) ? Number(scored.correct_index) : null,
      yourIndex: Number.isFinite(selectedIndex) ? selectedIndex : null,
    });
  }

  await sleep(REVEAL_MS);
  // Return the shape the loop expects for state advancement.
  return {
    score: Number(scored.run?.score ?? state.score),
    answered: Number(scored.run?.answered ?? state.answered + 1),
    score_awarded: Number(scored.score_awarded || 0),
  };
}

// ----- render helpers ------------------------------------------------------

function paintAnswering(page, question, state, { deadline }) {
  const num = state.answered + 1;
  const total = state.total;
  const remainingMs = Math.max(0, deadline - performance.now());
  const startPct = Math.max(0, Math.min(100, (remainingMs / state.windowMs) * 100));
  const imageHtml = question.image_url
    ? `<img src="${escape(question.image_url)}" alt=""
         style="width:100%;border-radius:10px;margin:6px 0 10px" />`
    : '';
  // Prompt + option grid render inside a closed shadow root (see
  // lib/question_view.js); the mount happens in runOneQuestion's
  // mountAnsweringView(). Timer, progress bar, 50/50 HUD, image, and
  // running-score line stay in light DOM — they are not attack surface
  // for the observed userscripts (bots key off the option text).
  page.innerHTML = `
    <div class="card" style="padding:14px;">
      <div class="row">
        <span class="muted" style="font-size:11px;">Question ${num} of ${total}</span>
        <div class="sp"></div>
        <span id="duel-timer-secs"
              style="font-family:var(--font-display);font-size:13px;color:#fff;">
          ${(remainingMs / 1000).toFixed(1)}s
        </span>
      </div>
      <div style="height:4px;background:#1a1432;border-radius:3px;margin:8px 0 12px;overflow:hidden;">
        <div id="duel-progress-bar"
             style="height:100%;width:${startPct}%;
                    background:linear-gradient(90deg,#5B2C91,#E94BA2);
                    transition:width ${TICK_MS}ms linear;">
        </div>
      </div>
      ${fiftyfiftyHudHtml(state, question)}
      ${imageHtml}
      <div class="qv-slot" style="position:relative;">
        <div id="duel-qv-host" style="position:relative;z-index:1;"></div>
      </div>
    </div>
    <div class="center" style="font-size:11px;color:var(--mute);margin-top:10px">
      Running score: <b style="color:#fff">${state.score}</b>
    </div>
  `;
}

/**
 * 50/50 button + status pill rendered inside the question card. Mirrors
 * live_room's `fiftyfiftyHudHtml`: enabled only when the player has
 * inventory AND hasn't spent their per-duel allowance yet AND hasn't
 * used 50/50 on this specific question. Renders a "used" pill instead
 * once the cap is hit so the player knows the power-up is spent.
 */
function fiftyfiftyHudHtml(state, question) {
  const cap = 1;  // FIFTYFIFTY_PER_DUEL — one spend per duel
  const usedCount = (state.fiftyfiftyUsedOrders || []).length;
  const usedThisQ = (state.fiftyfiftyUsedOrders || []).includes(question.order);
  const balance = Math.max(0, Number(state.fiftyfiftyBalance || 0));
  const remaining = Math.max(0, cap - usedCount);
  const canTap = remaining > 0 && balance > 0 && !usedThisQ
    && !state.submittingFiftyfifty;

  if (usedCount > 0) {
    return `
      <div class="row" style="margin:0 0 10px;align-items:center;gap:10px;">
        <span style="display:inline-flex;align-items:center;gap:6px;padding:6px 10px;background:rgba(233,75,162,0.10);border:1px solid rgba(233,75,162,0.4);border-radius:8px;font-family:var(--font-display);font-size:11px;font-weight:700;color:#E94BA2;letter-spacing:0.08em;text-transform:uppercase;">
          🎯 50/50 used
        </span>
        <span class="muted" style="font-size:11.5px;">
          two wrong answers removed
        </span>
      </div>
    `;
  }
  if (balance <= 0) return '';

  const lblHint = `${remaining}/${cap} left this duel · ${balance} owned`;
  return `
    <div class="row" style="margin:0 0 10px;align-items:center;gap:10px;">
      <button type="button" id="duel-ff-use-btn" class="btn"
              ${canTap ? '' : 'disabled'}
              style="padding:6px 12px;font-size:12px;font-family:var(--font-display);
                     background:${canTap ? 'rgba(233,75,162,0.18)' : 'rgba(255,255,255,0.04)'};
                     border:1px solid ${canTap ? 'rgba(233,75,162,0.55)' : '#2A2244'};
                     color:${canTap ? '#fff' : 'var(--mute)'};
                     letter-spacing:0.06em;text-transform:uppercase;font-weight:700;">
        🎯 Use 50/50
      </button>
      <span class="muted" style="font-size:11.5px;">
        ${escape(lblHint)}
      </span>
    </div>
  `;
}

function wireFiftyfifty(page, state, question, { onDone }) {
  const btn = page.querySelector('#duel-ff-use-btn');
  if (!btn || btn.disabled) return;
  btn.addEventListener('click', async () => {
    if (state.submittingFiftyfifty) return;
    state.submittingFiftyfifty = true;
    btn.disabled = true;
    try {
      const res = await api(`/duels/${state.duelId}/fiftyfifty`, {
        method: 'POST',
        body: { question_order: question.order },
      });
      state.eliminatedByOrder[question.order] = Array.isArray(res.eliminated_indices)
        ? res.eliminated_indices.slice()
        : [];
      if (!state.fiftyfiftyUsedOrders.includes(question.order)) {
        state.fiftyfiftyUsedOrders.push(question.order);
      }
      state.fiftyfiftyBalance = Number(res.fiftyfifty_balance || 0);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not use 50/50.';
      toast.error(msg);
      btn.disabled = false;
    } finally {
      state.submittingFiftyfifty = false;
      onDone();
    }
  });
}

function paintReveal(page, question, {
  picked,
  isCorrect,
  scoreAwarded,
  totalScore,
  questionNum,
  total,
}) {
  const imageHtml = question.image_url
    ? `<img src="${escape(question.image_url)}" alt=""
         style="width:100%;border-radius:10px;margin:6px 0 10px" />`
    : '';
  // Reveal prompt + choices (correct-green, your-pink) render inside the
  // shared closed-shadow view; runOneQuestion mounts it against
  // #duel-qv-host after this scaffold is written.
  const scoreLine = picked == null
    ? `<div class="muted" style="font-size:12px;margin-top:12px;">
         No answer this round.
       </div>`
    : `<div style="margin-top:12px;font-family:var(--font-display);font-size:13px;color:${isCorrect ? '#5FE0BE' : '#FF8FB6'};">
         ${isCorrect ? `✓ +${scoreAwarded} this question` : '✗ Wrong — 0 this question'}
         <span style="color:#fff;opacity:0.8;margin-left:6px">· ${totalScore} total</span>
       </div>`;

  page.innerHTML = `
    <div class="card" style="padding:14px;">
      <div class="row">
        <span class="muted" style="font-size:11px;">Question ${questionNum} of ${total} · reveal</span>
        <div class="sp"></div>
        <span style="font-family:var(--font-display);font-size:13px;color:#fff;">
          next in ${(REVEAL_MS / 1000).toFixed(1)}s
        </span>
      </div>
      ${imageHtml}
      <div class="qv-slot" style="position:relative;">
        <div id="duel-qv-host" style="position:relative;z-index:1;"></div>
      </div>
      ${scoreLine}
    </div>
  `;
}

function updateTimer(page, remainingMs, windowMs) {
  const t = page.querySelector('#duel-timer-secs');
  if (t) t.textContent = `${(remainingMs / 1000).toFixed(1)}s`;
  const bar = page.querySelector('#duel-progress-bar');
  if (bar) {
    const pct = Math.max(0, Math.min(100, (remainingMs / windowMs) * 100));
    bar.style.width = `${pct}%`;
  }
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}
